import React, { useState, useMemo } from 'react';
import {
  Typography,
  useTheme,
  Card,
  CardContent,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Button, // Import Button
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';

// This is the display component for errors or when no data is available.
const ErrorDisplay = ({ onRefresh }: { onRefresh: () => void; }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    p: 3,
    height: '100%',
    minHeight: 300, // Ensure it has a minimum height
  }}>
    <img src="/usmerror.svg" alt="Error" style={{ width: '130px', marginBottom: '16px' }} />
    <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
      Unable to Display Data
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '300px', mb: 2 }}>
      We're having trouble loading activities right now. Please try again.
    </Typography>
    <Button
        variant="text"
        onClick={onRefresh}
        sx={{
            textTransform: 'none',
            color: '#0064FF',
            fontWeight: 500,
            fontFamily:'Inter',
            lineHeight:'21px',
            fontSize:'14px',
        }}
        >
        Refresh Page
        </Button>
  </Box>
);

export interface TimeSeriesGroup {
  name: string;
  cost: number;
}
export interface TimeSeriesDataPoint {
  date: string;
  groups: TimeSeriesGroup[];
}
export interface CostOverTimeChartProps {
  timeSeries: TimeSeriesDataPoint[];
  colorMap: { [key: string]: string };
  error?: any; // Prop to indicate an error occurred
  onRefresh?: () => void; // Prop for the refresh action
}

const CostOverTimeChart: React.FC<CostOverTimeChartProps> = ({ timeSeries, colorMap, error, onRefresh }) => {
  const theme = useTheme();
  const [view, setView] = useState('bar');

  const chartOption = useMemo(() => {
    // Return early if there's no data, the error state will be handled in the JSX
    if (!timeSeries || timeSeries.length === 0) {
      return {};
    }

    const groupTotals = new Map<string, number>();
    timeSeries.forEach(dataPoint => {
      dataPoint.groups.forEach(group => {
        const currentTotal = groupTotals.get(group.name) || 0;
        groupTotals.set(group.name, currentTotal + group.cost);
      });
    });

    const allGroupNames = Array.from(groupTotals.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([name]) => name);

    const dates = timeSeries.map(dp => dayjs(dp.date).format('MMM D'));

    const seriesData = new Map<string, (number | null)[]>();
    allGroupNames.forEach(group => seriesData.set(group, new Array(dates.length).fill(0)));
    const placeholderData = new Array(dates.length).fill(null);

    let maxDailyTotal = 0;
    timeSeries.forEach(dp => {
        let dailyTotal = 0;
        dp.groups.forEach(g => { dailyTotal += g.cost; });
        if (dailyTotal > maxDailyTotal) {
            maxDailyTotal = dailyTotal;
        }
    });
    const niceMaxValue = maxDailyTotal > 0 ? Math.ceil(maxDailyTotal / .85) : 10000;

    timeSeries.forEach((dataPoint, dateIndex) => {
      let dailyTotal = 0;
      dataPoint.groups.forEach(group => {
        dailyTotal += group.cost;
        const groupName = group.name;
        const currentData = seriesData.get(groupName);
        if (currentData) {
          (currentData[dateIndex] as number) += group.cost;
        }
      });

      if (dailyTotal === 0) {
        const minHeight = niceMaxValue * 0.50;
        const maxHeight = niceMaxValue * 0.85;
        placeholderData[dateIndex] = Math.random() * (maxHeight - minHeight) + minHeight;
      }
    });

    const series = allGroupNames.map((groupName, index) => ({
      name: groupName,
      type: view,
      stack: view === 'bar' ? 'total' : undefined,
      data: seriesData.get(groupName),
      color: colorMap[groupName] || theme.palette.grey[400],
      barMaxWidth: 12,
      emphasis: {
        focus: 'series'
      },
      ...(view === 'bar' && {
          itemStyle: {
              borderRadius: 0
          }
      }),
      ...(view === 'line' && {
          showSymbol: false,
      })
    }));

    if (view === 'bar') {
      series.unshift({
          name: 'placeholder',
          type: 'bar',
          stack: 'total',
          itemStyle: {
              color: theme.palette.grey[200],
          },
          data: placeholderData,
          barMaxWidth: 12,
      } as any);
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
            const isPlaceholder = params.some(p => p.seriesName === 'placeholder' && p.value > 0);
            if(isPlaceholder) return '';

            const dataIndex = params[0].dataIndex;
            // Use the index to get the full date string from your original timeSeries data.
            const originalDate = timeSeries[dataIndex].date;
            // Format the full date dynamically, including the correct day and year.
            const title = dayjs(originalDate).format('ddd, MMM D • YYYY');
            
            const content = params
                .filter(p => p.seriesName !== 'placeholder' && p.value > 0)
                .sort((a,b) => b.value - a.value)
                .slice(0, 5)
                .map(p => `
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
                      <div style="display: flex; align-items: center;">
                        <span style="font-size: 10px; color: ${p.color}; margin-right: 6px;">●</span>
                        <span style="font-size: 12px; font-weight: 500;">${p.seriesName}</span>
                      </div>
                      <span style="font-size: 12px; font-weight: 500;">$${(p.value as number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                `).join('');

            if (!content) return '';

            return `
                <div style="font-family: Inter; border-radius: 8px; padding: 12px; min-width: 220px; background-color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                  <div style="font-size: 12px; color: #888; margin-bottom: 12px; text-align: center;">${title}</div>
                  ${content}
                </div>
            `;
        },
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        shadowColor: 'transparent',
        padding: 0,
      },
      grid: { left: '1%', right: '1%', bottom: '0%', top: '20px', containLabel: true },
      xAxis: { type: 'category', data: dates, show: false },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: {
          formatter: (value: number) => (value >= 1000 ? `$${value / 1000}K` : `$${value}`),
          fontFamily: 'Inter',
          color: '#888',
          inside: false
        },
        splitLine: { lineStyle: { type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series,
      animation: true,
    };
  }, [timeSeries, colorMap, view, theme]);

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const buttonSx = {
    borderRadius: '8px !important',
    border: 'none',
    px: 1.2,
    py: 1.2,
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
      fontSize: '18px',
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
    },
  };

  const showErrorState = error || !timeSeries || timeSeries.length === 0;

  return (
    <Card elevation={0} sx={{ borderRadius: '16px', fontFamily: 'Inter, sans-serif' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: '550', fontSize: '18px' }}>Cost Over Time</Typography>
          {/* Hide toggle buttons if there's an error/no data */}
          {!showErrorState && (
            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} aria-label="chart view" sx={{ backgroundColor: theme.palette.grey[100], p: '3px', borderRadius: '12px' }}>
              <ToggleButton value="bar" aria-label="bar chart view" sx={buttonSx}><BarChartIcon /></ToggleButton>
              <ToggleButton value="line" aria-label="line chart view" sx={buttonSx}><ShowChartIcon /></ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        
        {/* Conditionally render the chart or the error/empty state */}
        {showErrorState ? (
            <ErrorDisplay onRefresh={onRefresh || (() => {})} />
        ) : (
            <Box sx={{ height: 300, width: '100%' }}>
              <ReactECharts
                option={chartOption}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
              />
            </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CostOverTimeChart;